import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, X, MessageSquare, Image as ImageIcon } from 'lucide-react';
import { STATUS_PALETTE } from './adminDesignSystem';
import { Badge } from './AdminPanelPrimitives';
import { formatEuropeanDateTime } from '../../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRow = ({ value, size = 14, color = '#F59E0B' }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }} aria-label={`${value} stars`}>
    {Array.from({ length: 5 }).map((_, idx) => (
      <Star key={idx} size={size} fill={idx < value ? color : 'transparent'} color={idx < value ? color : '#D1D5DB'} strokeWidth={2} />
    ))}
  </span>
);

// Tiny SVG sparkline for review velocity (last 8 weeks). Highlights the
// most recent bar so admins can spot a venue that's trending up.
const VelocitySparkline = ({ buckets = [], color }) => {
  if (!Array.isArray(buckets) || buckets.length === 0) return null;
  const width = 168;
  const height = 36;
  const gap = 3;
  const barWidth = (width - (buckets.length - 1) * gap) / buckets.length;
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const total = buckets.reduce((sum, b) => sum + b.count, 0);
  const recent = buckets[buckets.length - 1]?.count || 0;
  const prior = buckets[buckets.length - 2]?.count || 0;
  const trendUp = recent > prior;
  return (
    <div data-testid="admin-reviews-velocity-sparkline" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#6B7280' }}>Velocity · last 8 weeks</p>
        <p style={{ margin: 0, fontSize: 11, color: total === 0 ? '#9CA3AF' : trendUp ? '#0D682C' : '#6B7280', fontWeight: 700 }}>
          {total === 0 ? 'no recent reviews' : trendUp ? '↑ trending up' : recent < prior ? '↓ slowing' : 'flat'}
        </p>
      </div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="Reviews per week sparkline">
        {buckets.map((bucket, idx) => {
          const h = Math.max(2, (bucket.count / max) * (height - 4));
          const x = idx * (barWidth + gap);
          const y = height - h;
          const isRecent = idx === buckets.length - 1;
          return (
            <g key={idx}>
              <rect x={x} y={y} width={barWidth} height={h} rx={2} fill={isRecent ? color : `${color}55`}>
                <title>{`Week ${bucket.week_offset === 0 ? '(this week)' : `${bucket.week_offset}`}: ${bucket.count} ${bucket.count === 1 ? 'review' : 'reviews'}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const BreakdownBar = ({ stars, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div
      data-testid={`admin-reviews-breakdown-${stars}`}
      style={{ display: 'grid', gridTemplateColumns: '32px 1fr 56px', alignItems: 'center', gap: 10, fontSize: 12.5 }}
    >
      <span style={{ fontWeight: 700, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {stars}<Star size={11} fill={color} color={color} strokeWidth={2} />
      </span>
      <div style={{ height: 8, borderRadius: 9999, background: '#F3F4F6', overflow: 'hidden', position: 'relative' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 320ms ease' }} />
      </div>
      <span style={{ color: '#6B7280', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{count} ({pct}%)</span>
    </div>
  );
};

const ReviewCard = ({ review, onReply, replyingId, replyDraft, setReplyDraft, submittingReply }) => {
  const isReplying = replyingId === review.id;
  const hasReply = !!review.venue_reply;
  return (
    <div
      data-testid={`admin-review-card-${review.id}`}
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: 14,
        background: '#FFFFFF',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
          {review.guest_photo_url ? (
            <img
              src={review.guest_photo_url}
              alt=""
              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E5E7EB' }}
            />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: STATUS_PALETTE.neutral.soft, color: STATUS_PALETTE.neutral.text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
              {(review.guest_name || 'A').charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: '#0F1115' }}>{review.guest_name || 'Allure guest'}</p>
            <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#6B7280' }}>{formatEuropeanDateTime(review.created_at)}</p>
          </div>
        </div>
        <StarRow value={Number(review.rating) || 0} />
      </div>

      {review.review_text ? (
        <p style={{ margin: 0, fontSize: 13, color: '#3B404B', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{review.review_text}</p>
      ) : (
        <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>(No written review)</p>
      )}

      {Array.isArray(review.photos) && review.photos.length ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {review.photos.map((url) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer" style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
              <img src={url} alt="review attachment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </a>
          ))}
        </div>
      ) : null}

      {hasReply ? (
        <div style={{ marginTop: 4, padding: '10px 12px', borderRadius: 12, background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: STATUS_PALETTE.venue.text, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {review.venue_reply.author_role === 'admin' ? 'Allure team reply' : 'Venue reply'}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#374151', whiteSpace: 'pre-wrap' }}>{review.venue_reply.text}</p>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9CA3AF' }}>{formatEuropeanDateTime(review.venue_reply.created_at)}</p>
        </div>
      ) : null}

      {!hasReply && !isReplying ? (
        <button
          type="button"
          data-testid={`admin-review-reply-open-${review.id}`}
          onClick={() => onReply(review.id, 'open')}
          style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9999, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
        >
          <MessageSquare size={12} /> Reply as Allure
        </button>
      ) : null}

      {isReplying ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            value={replyDraft}
            onChange={(event) => setReplyDraft(event.target.value)}
            data-testid={`admin-review-reply-input-${review.id}`}
            placeholder="Write a public reply on behalf of Allure…"
            style={{ width: '100%', minHeight: 70, padding: 10, borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={submittingReply || !replyDraft.trim()}
              onClick={() => onReply(review.id, 'submit')}
              data-testid={`admin-review-reply-submit-${review.id}`}
              style={{ padding: '7px 14px', borderRadius: 9999, border: 'none', background: '#0F1115', color: '#FFFFFF', cursor: submittingReply || !replyDraft.trim() ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12.5, opacity: submittingReply || !replyDraft.trim() ? 0.5 : 1 }}
            >
              {submittingReply ? 'Posting…' : 'Post reply'}
            </button>
            <button
              type="button"
              onClick={() => onReply(review.id, 'cancel')}
              data-testid={`admin-review-reply-cancel-${review.id}`}
              style={{ padding: '7px 14px', borderRadius: 9999, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', cursor: 'pointer', fontWeight: 700, fontSize: 12.5 }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const AdminVenueReviewsModal = ({ venueId, venueName, onClose, headers }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [replyingId, setReplyingId] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    if (!venueId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`${API}/admin/venues/${venueId}/reviews`, { headers })
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Failed to load reviews');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [venueId, headers]);

  const summary = data?.summary || { count: 0, average: 0, breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, photos_count: 0, unreplied_count: 0, velocity: [] };
  const items = data?.items || [];

  const filteredItems = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'unreplied') return items.filter((review) => !review.venue_reply);
    if (filter === 'photos') return items.filter((review) => Array.isArray(review.photos) && review.photos.length);
    const stars = Number(filter);
    if (Number.isFinite(stars) && stars >= 1 && stars <= 5) {
      return items.filter((review) => Number(review.rating) === stars);
    }
    return items;
  }, [items, filter]);

  const handleReply = async (reviewId, action) => {
    if (action === 'open') {
      setReplyingId(reviewId);
      setReplyDraft('');
      return;
    }
    if (action === 'cancel') {
      setReplyingId('');
      setReplyDraft('');
      return;
    }
    const text = replyDraft.trim();
    if (!text) return;
    setSubmittingReply(true);
    try {
      const response = await axios.post(`${API}/venue-reviews/${reviewId}/reply`, { text }, { headers });
      const updatedReview = response.data?.review;
      setData((prev) => prev ? { ...prev, items: prev.items.map((entry) => (entry.id === reviewId ? { ...entry, ...updatedReview } : entry)), summary: { ...prev.summary, unreplied_count: Math.max(0, prev.summary.unreplied_count - 1) } } : prev);
      setReplyingId('');
      setReplyDraft('');
      toast.success('Reply posted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const tone = summary.average >= 4.5 ? 'active' : summary.average >= 3.5 ? 'venue' : summary.average >= 2.5 ? 'early' : 'urgent';
  const palette = STATUS_PALETTE[tone];

  return (
    <div
      data-testid="admin-venue-reviews-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,21,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1200 }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ width: 'min(960px, 100%)', maxHeight: '90vh', background: '#FFFFFF', borderRadius: 22, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 28px 80px rgba(15,17,21,0.35)' }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700, color: '#0F1115' }}>Reviews — {venueName || 'Venue'}</p>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#6B7280' }}>Inspect guest feedback, reply on behalf of Allure, and watch for trends.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-venue-reviews-modal-close"
            style={{ border: '1px solid #E5E7EB', borderRadius: 10, background: '#FFFFFF', padding: '7px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 12.5 }}
          >
            <X size={14} /> Close
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'minmax(0, 320px) 1fr', gap: 0, minHeight: 0 }}>
          {/* Summary rail */}
          <div style={{ padding: '18px 20px', borderRight: '1px solid #F1F3F5', background: '#FAFAFB', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              data-testid="admin-reviews-summary-card"
              style={{ padding: 14, borderRadius: 16, background: '#FFFFFF', border: `1px solid ${palette.solid}22`, boxShadow: `0 6px 18px ${palette.solid}11` }}
            >
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#6B7280' }}>Average rating</p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 32, fontWeight: 800, color: palette.text }} data-testid="admin-reviews-average">
                  {summary.average.toFixed(1)}
                </p>
                <span style={{ fontSize: 13, color: '#6B7280' }}>/ 5</span>
              </div>
              <div style={{ marginTop: 4 }}>
                <StarRow value={Math.round(summary.average)} size={15} color={palette.solid} />
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6B7280' }} data-testid="admin-reviews-count">
                Based on <strong>{summary.count}</strong> {summary.count === 1 ? 'review' : 'reviews'}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[5, 4, 3, 2, 1].map((stars) => (
                <BreakdownBar key={stars} stars={stars} count={summary.breakdown[String(stars)] || 0} total={summary.count} color={palette.solid} />
              ))}
            </div>

            <VelocitySparkline buckets={summary.velocity || []} color={palette.solid} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <Badge text={`${summary.photos_count} ${summary.photos_count === 1 ? 'photo' : 'photos'}`} tone="info" />
              <Badge
                text={summary.unreplied_count === 0 ? 'All replied' : `${summary.unreplied_count} unreplied`}
                tone={summary.unreplied_count === 0 ? 'active' : 'hold'}
              />
            </div>
          </div>

          {/* Reviews list */}
          <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-testid="admin-reviews-filter-row">
              {[
                { id: 'all', label: `All (${summary.count})` },
                { id: 'unreplied', label: `Unreplied (${summary.unreplied_count})` },
                { id: 'photos', label: `With photos (${summary.photos_count > 0 ? items.filter((r) => (r.photos || []).length).length : 0})` },
                { id: '5', label: '5★' },
                { id: '4', label: '4★' },
                { id: '3', label: '3★' },
                { id: '2', label: '2★' },
                { id: '1', label: '1★' },
              ].map((option) => {
                const active = filter === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setFilter(option.id)}
                    data-testid={`admin-reviews-filter-${option.id}`}
                    style={{ padding: '6px 12px', borderRadius: 9999, border: `1px solid ${active ? '#0F1115' : '#E5E7EB'}`, background: active ? '#0F1115' : '#FFFFFF', color: active ? '#FFFFFF' : '#3B404B', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {loading ? (
              <p style={{ margin: '32px 0', textAlign: 'center', color: '#6B7280', fontSize: 13 }} data-testid="admin-reviews-loading">Loading reviews…</p>
            ) : error ? (
              <p style={{ margin: '32px 0', textAlign: 'center', color: STATUS_PALETTE.urgent.text, fontSize: 13 }} data-testid="admin-reviews-error">{error}</p>
            ) : filteredItems.length === 0 ? (
              <p style={{ margin: '32px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }} data-testid="admin-reviews-empty">
                {summary.count === 0 ? 'No reviews yet for this venue.' : 'No reviews match the current filter.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="admin-reviews-list">
                {filteredItems.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onReply={handleReply}
                    replyingId={replyingId}
                    replyDraft={replyDraft}
                    setReplyDraft={setReplyDraft}
                    submittingReply={submittingReply}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVenueReviewsModal;
